import React, { useState, useRef } from "react";

const BIZ_LABELS = {
    retail:      "Retail",
    distributor: "Distributor",
    cultivator:  "Cultivator",
};

const INDUSTRY_LABELS = {
    hemp:         "Hemp",
    rec_adult_use:"Rec / Adult Use",
    medical:      "Medical",
};

const CompanyPipelineCard = ({
    companyName,
    companyId,
    businessType,
    industryType,
    stageColor,
    contacts,
    onSelectLead,
    onSelectCompany,
    onDragStart,
    onDragEnd,
}) => {
    const [expanded, setExpanded] = useState(false);
    const clickTimer = useRef(null);

    // Single-click on header: toggle contacts (delayed so double-click can cancel it)
    const handleHeaderClick = () => {
        if (clickTimer.current) {
            clearTimeout(clickTimer.current);
            clickTimer.current = null;
            return;
        }
        clickTimer.current = setTimeout(() => {
            clickTimer.current = null;
            setExpanded((v) => !v);
        }, 220);
    };

    // Double-click anywhere on card: navigate to company page
    const handleDoubleClick = (e) => {
        e.stopPropagation();
        if (clickTimer.current) {
            clearTimeout(clickTimer.current);
            clickTimer.current = null;
        }
        if (companyId && onSelectCompany) onSelectCompany(companyId);
    };

    return (
        <div
            className="crm-company-card"
            style={{ borderLeftColor: stageColor || "var(--color-primary)" }}
            draggable
            onDragStart={onDragStart}
            onDragEnd={onDragEnd}
            onDoubleClick={handleDoubleClick}
        >
            {/* ── Header — always visible. Click to expand contacts ── */}
            <div className="crm-company-card__header" onClick={handleHeaderClick}>
                <span className="crm-company-card__drag-handle">⠿</span>

                <div className="crm-company-card__meta">
                    <span className="crm-company-card__name">{companyName}</span>
                    {(businessType || industryType) && (
                        <div className="crm-company-card__chips">
                            {businessType && (
                                <span className="crm-company-chip crm-company-chip--biz">
                                    {BIZ_LABELS[businessType] || businessType}
                                </span>
                            )}
                            {industryType && (
                                <span className="crm-company-chip crm-company-chip--industry">
                                    {INDUSTRY_LABELS[industryType] || industryType}
                                </span>
                            )}
                        </div>
                    )}
                </div>

                <div className="crm-company-card__header-right">
                    <span className="crm-company-card__count">{contacts.length}</span>
                    <span className={`crm-company-card__chevron ${expanded ? "open" : ""}`}>›</span>
                </div>
            </div>
            
            {/* ── Contact list (collapse/expand) ────────────────── */}
            {expanded && (
                <div className="crm-company-card__contacts">
                    {contacts.length === 0 && (
                        <p className="crm-company-card__empty">No contacts</p>
                    )}
                    {contacts.map((lead, idx) => {
                        const isPoC      = idx === 0;
                        const hasReplied = !!lead.lastRepliedAt;
                        const hasOpened  = !!lead.lastOpenedAt && !hasReplied;
                        const hasBadges  = isPoC || hasReplied || hasOpened;
                        
                        return (
                            <div
                                key={lead.id}
                                className={`crm-contact-row ${isPoC ? "crm-contact-row--poc" : ""}`}
                                onClick={(e) => { e.stopPropagation(); onSelectLead(lead.id); }}
                            >
                                <div className="crm-contact-row__info">
                                    <span className="crm-contact-row__name">
                                        {lead.firstName} {lead.lastName}
                                    </span>
                                    {lead.jobTitle && (
                                        <span className="crm-contact-row__title">{lead.jobTitle}</span>
                                    )}
                                </div>

                                {hasBadges && (
                                    <div className="crm-contact-row__badges">
                                        {isPoC && (
                                            <span className="crm-contact-badge crm-contact-badge--poc">POC</span>
                                        )}
                                        {hasReplied && (
                                            <span className="crm-contact-badge crm-contact-badge--replied">Replied</span>
                                        )}
                                        {hasOpened && (
                                            <span className="crm-contact-badge crm-contact-badge--opened">Opened</span>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default CompanyPipelineCard;
